import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { Checkbox } from 'foundations-components/transitional/components/Checkbox';
import { ResultsMessage } from 'foundations-components/transitional/components/ResultsMessage';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { ListsIcon } from 'foundations-assets/icons/components/ListsIcon';
import { useTheme } from 'foundations-theming/utils';
import { getSortedFlattenedTeamsList } from '../utils/TeamUtils';
import { NestableList } from './common/NestableList';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const FALLBACK_CHECKBOX_WIDTH = 24;
const LINE_WIDTH = 1;
const TeamCheckbox = ({
  id,
  name,
  checked,
  onRemoveTeam,
  onAddTeam,
  disabled
}) => {
  const isMac = navigator.userAgent.includes('Mac');
  const tooltipText = useMemo(() => {
    if (isMac) {
      return 'Click to select this Team and all its descendants. \n\nHold ⌘ and click to select this Team only.'; // Mac command symbol
    } else {
      return 'Click to select this Team and all its descendants. \n\nHold Ctrl and click to select this Team only.'; // Windows/Linux
    }
  }, [isMac]);
  return /*#__PURE__*/_jsx(Flex, {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "start",
    gap: "200",
    children: /*#__PURE__*/_jsx(Tooltip, {
      title: tooltipText,
      placement: "top",
      delay: 2000,
      delayOnHold: 700,
      textWrap: "pretty"
      // @ts-expect-error - valid style, applies correctly, types flag it incorrectly
      ,
      innerCssStyles: {
        whiteSpace: 'pre-wrap'
      },
      children: /*#__PURE__*/_jsx(Checkbox, {
        value: id,
        checked: checked,
        paddingInlineEnd: "100",
        inlineSize: "fit-content",
        alignment: "center",
        justifyContent: "center",
        disabled: disabled,
        "data-test-id": "team-checkbox",
        onChange: e => {
          if (checked) {
            onRemoveTeam(id);
          } else {
            if (isMac && e.nativeEvent.metaKey) {
              onAddTeam(id, false);
            } else if (!isMac && e.nativeEvent.ctrlKey) {
              onAddTeam(id, false);
            } else {
              onAddTeam(id, true);
            }
          }
        },
        children: /*#__PURE__*/_jsxs(Flex, {
          "data-test-id": `assign-team-checkbox-${id}`,
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "start",
          gap: "200",
          children: [/*#__PURE__*/_jsx(ListsIcon, {}), /*#__PURE__*/_jsx(Text, {
            children: name
          })]
        })
      })
    })
  });
};
const SelectableTeamsList = ({
  teams,
  selectedTeamIds = [],
  onAddTeam,
  onRemoveTeam,
  rootLevelTeamIds,
  userHasTeamAssignmentScope,
  allowedTeamIds,
  DisallowedTeamFormattedMessage
}) => {
  const {
    space
  } = useTheme();
  const containerRef = useRef(null);
  const [indentSize, setIndentSize] = useState(undefined);
  useLayoutEffect(() => {
    if (containerRef.current) {
      const firstCheckbox = containerRef.current.querySelector('span[class^=Checkbox__CheckboxIndicator]');
      if (firstCheckbox) {
        const checkboxWidth = Math.round(firstCheckbox.getBoundingClientRect().width);
        setIndentSize(checkboxWidth + parseInt(space[100], 10) - LINE_WIDTH);
      }
    }
  }, [space]);
  const renderTeamCheckbox = useCallback(team => {
    const {
      id,
      name
    } = team;
    const checked = selectedTeamIds.includes(id);
    const isRootLevelTeam = rootLevelTeamIds.includes(id);
    const isDisabled = isRootLevelTeam && checked && !userHasTeamAssignmentScope;
    const isTeamDisallowed = allowedTeamIds && !allowedTeamIds.includes(id);
    const key = `${id}-team-checkbox`;
    const checkbox = /*#__PURE__*/_jsx(TeamCheckbox, {
      id: id,
      name: name,
      checked: checked,
      disabled: isDisabled || isTeamDisallowed,
      onAddTeam: onAddTeam,
      onRemoveTeam: onRemoveTeam
    }, key);
    if (isDisabled || isTeamDisallowed) {
      return /*#__PURE__*/_jsx(Tooltip, {
        placement: "left",
        title: isTeamDisallowed ? DisallowedTeamFormattedMessage ? /*#__PURE__*/_jsx(DisallowedTeamFormattedMessage, {
          teamId: id,
          teamName: name
        }) : null : /*#__PURE__*/_jsx(FormattedMessage, {
          message: 'ui-asset-management-lib.tooltips.disabledTeamTooltip'
        }),
        children: /*#__PURE__*/_jsx("span", {
          children: checkbox
        })
      }, key);
    }
    return checkbox;
  }, [onAddTeam, onRemoveTeam, rootLevelTeamIds, selectedTeamIds, userHasTeamAssignmentScope, allowedTeamIds, DisallowedTeamFormattedMessage]);
  const teamOptions = getSortedFlattenedTeamsList(teams);
  if (!teamOptions.length) {
    return /*#__PURE__*/_jsx(ResultsMessage, {
      illustration: "empty-state-charts",
      illustrationProps: {
        width: 187
      },
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "ui-asset-management-lib.labels.noTeams"
      })
    });
  }
  return /*#__PURE__*/_jsx("div", {
    ref: containerRef,
    children: /*#__PURE__*/_jsx(NestableList, {
      items: teamOptions,
      renderItem: team => renderTeamCheckbox(team),
      indentSize: indentSize !== null && indentSize !== void 0 ? indentSize : FALLBACK_CHECKBOX_WIDTH
    })
  });
};
export default SelectableTeamsList;